"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowUpRight, Calendar, Compass, FileText, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ConsultationCta() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15
  });
  return <section id="consultationcta" data-nav="light" ref={ref} className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {/* Visual column: Realistic interior renovation photography */}
          <div className="lg:col-span-6 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-4/3 sm:aspect-16/11 bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_kitchen_renovation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_kitchen_renovation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_kitchen_renovation.png-wvc-sizes" alt="Luxury kitchen renovation with custom island, quartz countertops, and designer pendant lighting" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-linear-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>

          {/* Action column */}
          <div className="lg:col-span-6 flex flex-col justify-center">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-[1.15] mb-6">
              Start your home transformation with Brookhaven Elevation Renovations
            </h2>

            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-8 max-w-xl">
              From historic Brookhaven cottages to modern estate renovations, our team provides
              disciplined craftsmanship, tailored architectural plans, and transparent pricing.
            </p>

            {/* Primary Action */}
            <div className="mb-10">
              <Button asChild size="lg" className="rounded-md font-medium text-base shadow-sm">
                <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-2">
                  <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                  <span>Request an Estimate</span>
                </Link>
              </Button>
            </div>

            {/* Secondary Text Links: Related Services */}
            <div className="border-t border-border pt-6 mb-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-3">
                Related Services
              </span>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-medium">
                <Link to="/WholeHomeInteriorRemodelingPage" className="inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                  <Compass className="size-4 text-primary" />
                  <span>Whole-Home Remodeling</span>
                </Link>
                <span className="text-border" aria-hidden="true">•</span>
                <Link to="/KitchenRemodelingPage" className="inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                  <Compass className="size-4 text-primary" />
                  <span>Kitchen Remodeling</span>
                </Link>
              </div>
            </div>

            {/* Planning & Resource Links */}
            <div className="border-t border-border pt-6">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-3">
                Planning Resources
              </span>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
                <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors underline-offset-4 hover:underline">
                  <FileText className="size-3.5" />
                  <span>Remodeling Cost Guide</span>
                  <ArrowUpRight className="size-3" />
                </Link>
                <span className="text-border" aria-hidden="true">•</span>
                <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors underline-offset-4 hover:underline">
                  <FileText className="size-3.5" />
                  <span>Remodeling Planning Guide</span>
                  <ArrowUpRight className="size-3" />
                </Link>
                <span className="text-border" aria-hidden="true">•</span>
                <Link to="/ContactRequestAnEstimatePage" className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors underline-offset-4 hover:underline">
                  <HelpCircle className="size-3.5" />
                  <span>FAQ</span>
                  <ArrowUpRight className="size-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}